import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { TIME_SLOTS, calcDurationHours, getAutoDiscount, getEndSlots, isBulkDayPassDiscount } from '../../utils/timeSlots';
import {
  usePromotion, offerLive, dayPassFreeQuantity, meetingRoomFreeHours,
  DAY_PASS_OFFER, MEETING_ROOM_OFFER, CREDITS_2X_OFFER, MEETING_ROOM_TYPES,
} from '../../lib/promotions';
import { useAuth } from '../../context/AuthContext';
import { addDays, format } from 'date-fns';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus, UserPlus, Coins, Ban, Building2, RefreshCw, Package, Eye, EyeOff, Pencil, Trash2, Briefcase, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const validateNotSunday = (dateStr) => {
  if (!dateStr) return true;
  const d = new Date(dateStr + 'T00:00:00');
  return d.getDay() !== 0;
};

// Dedicated Desk (monthly) — mirrors backend compute_monthly_end_date (31-day term).
const monthlyPeriodLabel = (bookingDateStr) => {
  if (!bookingDateStr) return null;
  const start = new Date(bookingDateStr + 'T00:00:00');
  const end = addDays(start, 31);
  return `${format(start, 'PP')} → ${format(end, 'PP')}`;
};

// Client-side pricing for one walk-in slot — mirrors the backend's
// `_price_guest_slot` math so the CM sees a live total before submitting.
// Parking rate source for one vehicle type: venue config when enabled, otherwise a
// CM-entered custom rate (`customRate`).
const getParkingRate = (cfg, isDaily, customRate) => {
  if (cfg?.enabled) return { configured: true, rate: parseFloat(isDaily ? cfg.daily_rate : cfg.hourly_rate) || 0 };
  return { configured: false, rate: parseFloat(customRate) || 0 };
};

const computeSlotPricing = (slot, bookables, venues = []) => {
  const selectedBookable = bookables.find(b => b.id === slot.bookable_id);
  const hours = calcDurationHours(slot.start_time, slot.end_time);
  const mode = selectedBookable?.booking_mode || 'hourly';
  const rate = selectedBookable?.hourly_rate || 0;
  const passQty = Math.max(1, parseInt(slot.pass_quantity) || 1);
  const isBulkPass = mode === 'daily' && isBulkDayPassDiscount(passQty);
  const baseAmount = mode === 'hourly' ? rate * hours : mode === 'daily' ? rate * passQty : rate;
  const autoDiscHourly = mode === 'hourly' ? getAutoDiscount(hours) : { percent: 0, label: '' };
  const autoDisc = isBulkPass
    ? { percent: Math.max(autoDiscHourly.percent, 10), label: 'Bulk Day Pass (10%)' }
    : autoDiscHourly;
  const autoDiscountAmt = baseAmount * autoDisc.percent / 100;
  const afterAutoDiscount = baseAmount - autoDiscountAmt;
  const discountPct = Math.max(0, Math.min(100, parseFloat(slot.discount_percent) || 0));
  const manualDiscountAmt = afterAutoDiscount * discountPct / 100;
  const discountedBase = afterAutoDiscount - manualDiscountAmt;
  const pcfg = venues.find(v => v.id === slot.venue_id)?.parking_config || {};
  const parkingUnits = mode === 'daily' ? passQty : 1;
  // CM guest bookings bill parking per day (not per hour), for every booking mode.
  const p4 = getParkingRate(pcfg.four_wheeler, true, slot.parking_4w_rate);
  const p2 = getParkingRate(pcfg.two_wheeler, true, slot.parking_2w_rate);
  const parking4wAmt = (parseInt(slot.parking_qty) || 0) * p4.rate * parkingUnits;
  const parking2wAmt = (parseInt(slot.parking_2w_qty) || 0) * p2.rate * parkingUnits;
  const parkingAmt = parking4wAmt + parking2wAmt;
  const gstAmount = (discountedBase + parkingAmt) * 0.18;
  const total = discountedBase + parkingAmt + gstAmount;
  return { parking4wAmt, parking2wAmt, parkingAmt, selectedBookable, hours, mode, rate, passQty, isBulkPass, baseAmount, autoDisc, autoDiscountAmt, discountPct, manualDiscountAmt, discountedBase, gstAmount, total };
};

const rewardDateLabel = (iso) => (iso ? format(new Date(iso), 'd MMM yyyy') : '');

// Whose account a walk-in sale offer would credit — GET /promotions/offer-customer,
// debounced while the CM types the email. Nothing is created by the lookup.
const useOfferCustomer = (email) => {
  const { api } = useAuth();
  const [state, setState] = useState({ loading: false, data: null });
  const trimmed = (email || '').trim();
  const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  useEffect(() => {
    if (!looksValid) { setState({ loading: false, data: null }); return undefined; }
    let cancelled = false;
    setState(s => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      api.get('/promotions/offer-customer', { params: { email: trimmed } })
        .then(r => { if (!cancelled) setState({ loading: false, data: r.data }); })
        .catch(() => { if (!cancelled) setState({ loading: false, data: null }); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [api, trimmed, looksValid]);
  return { ...state, looksValid };
};

// What the account lookup means for the CM, in one line.
const OfferAccountStatus = ({ email, lookup, what = 'rewards' }) => {
  if (!lookup.looksValid) {
    return <p className="text-xs text-amber-700 dark:text-amber-300">{email ? 'Enter a valid email address.' : `Enter the customer's email. The ${what} go to their Clayworks account.`}</p>;
  }
  if (lookup.loading || !lookup.data) return <p className="text-xs text-muted-foreground">Checking account…</p>;
  const d = lookup.data;
  if (!d.eligible) return <p className="text-xs text-red-600" data-testid="offer-account-problem">{d.reason}</p>;
  if (d.has_account) return <p className="text-xs text-green-700 dark:text-green-400" data-testid="offer-account-existing">✓ Existing customer account{d.name ? `: ${d.name}` : ''}. The {what} will be added to it.</p>;
  return <p className="text-xs text-muted-foreground" data-testid="offer-account-new">No account yet. One will be created for this email, and the customer will get an email to set their password.</p>;
};

// Sale reward one walk-in slot earns — mirrors backend _quote_walk_in_reward
// (the backend's numbers are what actually gets granted).
const slotOfferPreview = (priced, promo) => {
  const { selectedBookable, mode, passQty, hours } = priced;
  if (!selectedBookable) return null;
  if (selectedBookable.bookable_type === 'day_pass' && mode === 'daily' && offerLive(DAY_PASS_OFFER)) {
    const free = dayPassFreeQuantity(passQty);
    const min = promo.offers[DAY_PASS_OFFER].block || 3;
    return free
      ? { ok: true, text: `Earns ${free} free Day Pass${free !== 1 ? 'es' : ''}, usable after the paid pass${passQty !== 1 ? 'es' : ''}` }
      : { ok: false, text: `Add ${min - passQty} more pass${min - passQty !== 1 ? 'es' : ''} (${min} or more) to earn as many free Day Passes` };
  }
  if (mode === 'hourly' && MEETING_ROOM_TYPES.includes(selectedBookable.bookable_type) && offerLive(MEETING_ROOM_OFFER)) {
    if (!hours) return null;
    const free = meetingRoomFreeHours(hours);
    const block = promo.offers[MEETING_ROOM_OFFER].block || 2;
    return free
      ? { ok: true, text: `Earns ${free} free hrs as Meeting Room Credits` }
      : { ok: false, text: `Book ${block} or more hours to earn ${block} free hours` };
  }
  return { ok: false, text: 'This space isn\'t part of the sale offer' };
};

export const WalkInModal = ({ open, onClose, form, setForm, onSubmit, saving, venues, bookables, theme, holidayDates = {} }) => {
  const promo = usePromotion(); // re-price once sale promotion state loads
  const offerAvailable = offerLive(DAY_PASS_OFFER) || offerLive(MEETING_ROOM_OFFER);
  const applyOffer = offerAvailable && !!form.apply_sale_offer;
  const offerAccount = useOfferCustomer(applyOffer ? form.customer_email : '');
  const slots = form.slots || [];
  const updateSlot = (index, patch) => {
    setForm({ ...form, slots: slots.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };
  const addSlot = () => setForm({ ...form, slots: [...slots, {
    _key: Math.random().toString(36).slice(2),
    venue_id: '', bookable_id: '', start_time: '', end_time: '',
    booking_date: slots[0]?.booking_date || new Date().toISOString().split('T')[0],
    discount_percent: 0, parking_qty: 0, parking_2w_qty: 0, pass_quantity: 1, pass_days: 1,
  }] });
  const removeSlot = (index) => setForm({ ...form, slots: slots.filter((_, i) => i !== index) });

  const pricedSlots = slots.map(s => computeSlotPricing(s, bookables, venues));
  const grandTotal = pricedSlots.reduce((sum, p) => sum + p.total, 0);

  return (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-lg max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Create Walk-in / Guest Booking</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2">
          <Label>Customer Name *</Label>
          <Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} className={theme.inputBg} placeholder="Enter customer name" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email{applyOffer ? ' *' : ''}</Label>
            <Input type="email" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})} className={theme.inputBg} placeholder="customer@email.com" data-testid="walkin-email-input" />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})} className={theme.inputBg} placeholder="+91 98765 43210" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>GSTIN <span className="text-muted-foreground font-normal text-xs">(Optional · for B2B clients)</span></Label>
          <Input value={form.gstin || ''} onChange={(e) => setForm({...form, gstin: e.target.value.toUpperCase()})} maxLength={15} placeholder="22AAAAA0000A1Z5" className={theme.inputBg} data-testid="walkin-gstin-input" />
          <div className="grid grid-cols-1 gap-2 mt-2 p-3 rounded-md border border-primary/20 bg-primary/5">
            <div className="space-y-1">
              <Label className="text-xs">Company Name {form.gstin?.trim() ? '*' : <span className="text-muted-foreground font-normal">(Optional · appears on invoice)</span>}</Label>
              <Input value={form.company_name || ''} onChange={(e) => setForm({...form, company_name: e.target.value})} placeholder="Registered company name" className={theme.inputBg} data-testid="walkin-company-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Company Address {form.gstin?.trim() ? '*' : <span className="text-muted-foreground font-normal">(Optional · appears on invoice)</span>}</Label>
              <Input value={form.company_address || ''} onChange={(e) => setForm({...form, company_address: e.target.value})} placeholder="Billing address" className={theme.inputBg} data-testid="walkin-company-address-input" />
            </div>
          </div>
        </div>

        {offerAvailable && (
          <div className={`rounded-lg border p-3 space-y-2 ${applyOffer ? 'border-amber-500/60 bg-amber-500/10' : 'border-amber-500/30 bg-amber-500/5'}`} data-testid="walkin-sale-offer">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">🪔 Apply the {promo?.name || 'sale'} offer</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Same as online:
                  {offerLive(DAY_PASS_OFFER) && ` Day Pass Buy X, Get X Free (${promo.offers[DAY_PASS_OFFER].block || 3}+ passes).`}
                  {offerLive(MEETING_ROOM_OFFER) && ` Meeting rooms +${promo.offers[MEETING_ROOM_OFFER].block || 2} free hrs (${promo.offers[MEETING_ROOM_OFFER].block || 2}+ hrs).`}
                  {' '}Rewards are added to the customer's account once paid (cash or link) and are valid till {rewardDateLabel(promo?.reward_valid_until)}. Manual discounts are off with the offer.
                </p>
              </div>
              <Switch
                checked={applyOffer}
                onCheckedChange={(v) => setForm({ ...form, apply_sale_offer: v, slots: v ? slots.map(s => ({ ...s, discount_percent: 0 })) : slots })}
                data-testid="walkin-apply-offer-switch"
              />
            </div>
            {applyOffer && <OfferAccountStatus email={form.customer_email} lookup={offerAccount} />}
          </div>
        )}

        {slots.map((slot, index) => {
          const priced = pricedSlots[index];
          const { parking4wAmt, parking2wAmt, selectedBookable, hours, mode, rate, passQty, autoDisc, autoDiscountAmt, discountPct, manualDiscountAmt, discountedBase, gstAmount, total } = priced;
          return (
            <div key={slot._key || index} className={`rounded-lg border ${theme.cardBorder} p-3 space-y-3`} data-testid={`walkin-slot-${index}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Slot {index + 1}</p>
                {slots.length > 1 && (
                  <button type="button" onClick={() => removeSlot(index)} className="text-muted-foreground hover:text-red-500" title="Remove this slot" data-testid={`walkin-slot-remove-${index}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Venue *</Label>
                  <Select value={slot.venue_id} onValueChange={(v) => updateSlot(index, { venue_id: v, bookable_id: '' })}>
                    <SelectTrigger className={theme.inputBg}><SelectValue placeholder="Select Venue" /></SelectTrigger>
                    <SelectContent>
                      {venues.filter(v => v.is_active).map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Space *</Label>
                  <Select value={slot.bookable_id} onValueChange={(v) => {
                    const sel = bookables.find(b => b.id === v);
                    const updates = { bookable_id: v };
                    if (sel?.booking_mode === 'daily') { updates.start_time = '09:00'; updates.end_time = '20:00'; }
                    else if (sel?.booking_mode === 'monthly') { updates.start_time = '09:00'; updates.end_time = '18:00'; }
                    else { updates.start_time = ''; updates.end_time = ''; }
                    updateSlot(index, updates);
                  }}>
                    <SelectTrigger className={theme.inputBg}><SelectValue placeholder="Select Space" /></SelectTrigger>
                    <SelectContent>
                      {bookables.filter(b => b.venue_id === slot.venue_id && b.is_active).map(b => <SelectItem key={b.id} value={b.id}>{b.name} — ₹{b.hourly_rate}{b.booking_mode === 'daily' ? '/day' : b.booking_mode === 'monthly' ? '/month' : '/hr'}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={slot.booking_date || ''} onChange={(e) => { if (!validateNotSunday(e.target.value)) { toast.error('Closed on Sundays'); return; } if (holidayDates[e.target.value]) { toast.error(`Closed on ${e.target.value} (${holidayDates[e.target.value]})`); return; } updateSlot(index, { booking_date: e.target.value }); }} min={new Date().toISOString().split('T')[0]} className={theme.inputBg} data-testid={`walkin-date-input-${index}`} />
              </div>
              {mode === 'daily' ? (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20" data-testid={`walkin-daypass-info-${index}`}>
                    <p className="text-sm font-medium">Day Pass: 9:00 AM - 8:00 PM</p>
                    <p className="text-xs text-muted-foreground">Fixed timing for all day passes</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Quantity (passes) *</Label>
                      <Input type="number" min={1} max={100} value={slot.pass_quantity || 1}
                        onChange={(e) => updateSlot(index, { pass_quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                        className={theme.inputBg} data-testid={`walkin-pass-quantity-${index}`} />
                      <p className="text-xs text-muted-foreground">Number of day passes purchased</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Valid Days *</Label>
                      <Input type="number" min={1} max={365} value={slot.pass_days || 1}
                        onChange={(e) => updateSlot(index, { pass_days: Math.max(1, parseInt(e.target.value) || 1) })}
                        className={theme.inputBg} data-testid={`walkin-pass-days-${index}`} />
                      <p className="text-xs text-muted-foreground">Validity window only — does not multiply price</p>
                    </div>
                  </div>
                  {isBulkDayPassDiscount(slot.pass_quantity) && (
                    <div className="p-2.5 rounded bg-green-500/10 border border-green-500/30 text-xs text-green-800 dark:text-green-300">
                      🎉 <strong>Bulk Day Pass</strong> — 10% auto-discount applied (5+ passes).
                    </div>
                  )}
                </div>
              ) : mode === 'monthly' ? (
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20" data-testid={`walkin-monthly-info-${index}`}>
                  <p className="text-sm font-medium">Dedicated Desk — Monthly</p>
                  <p className="text-xs text-muted-foreground">Full month access, no time selection needed</p>
                  {slot.booking_date && (
                    <p className="text-xs font-medium text-primary mt-1.5">Access: {monthlyPeriodLabel(slot.booking_date)} (1 month)</p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time *</Label>
                    <Select value={slot.start_time} onValueChange={(v) => updateSlot(index, { start_time: v, end_time: '' })}>
                      <SelectTrigger className={theme.inputBg}><SelectValue placeholder="Start" /></SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>End Time *</Label>
                    <Select value={slot.end_time} onValueChange={(v) => updateSlot(index, { end_time: v })}>
                      <SelectTrigger className={theme.inputBg}><SelectValue placeholder="End" /></SelectTrigger>
                      <SelectContent>
                        {getEndSlots(slot.start_time).map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {selectedBookable && slot.start_time && slot.end_time && (mode !== 'hourly' || hours >= 1) && (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Base ({mode === 'hourly' ? `${hours.toFixed(2)}hrs × ₹${rate}` : mode === 'daily' ? `${passQty} pass × ₹${rate}` : 'Monthly Rate'})</span>
                    <span>₹{priced.baseAmount.toFixed(2)}</span>
                  </div>
                  {autoDisc.percent > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>{autoDisc.label}</span>
                      <span>-₹{autoDiscountAmt.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-primary/10">
                    <Label className="text-sm">Discount (%){applyOffer && <span className="text-xs text-muted-foreground font-normal"> · off with the sale offer</span>}</Label>
                    <Input type="number" min={0} max={100} step="0.1" value={slot.discount_percent ?? ''} disabled={applyOffer} onChange={(e) => { const v = parseFloat(e.target.value) || 0; updateSlot(index, { discount_percent: Math.max(0, Math.min(100, v)) }); }} className={`${theme.inputBg} w-24 h-8 text-sm`} placeholder="0" data-testid={`walkin-discount-input-${index}`} />
                  </div>
                  {discountPct > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Manual Discount ({discountPct}%)</span>
                      <span>-₹{manualDiscountAmt.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discounted Base</span>
                    <span>₹{discountedBase.toFixed(2)}</span>
                  </div>
                  {parking4wAmt > 0 && (
                    <div className="flex justify-between text-sm" data-testid={`walkin-parking-4w-line-${index}`}>
                      <span className="text-muted-foreground">4-Wheeler Parking ({slot.parking_qty} spot{slot.parking_qty > 1 ? 's' : ''})</span>
                      <span>₹{parking4wAmt.toFixed(2)}</span>
                    </div>
                  )}
                  {parking2wAmt > 0 && (
                    <div className="flex justify-between text-sm" data-testid={`walkin-parking-2w-line-${index}`}>
                      <span className="text-muted-foreground">2-Wheeler Parking ({slot.parking_2w_qty} spot{slot.parking_2w_qty > 1 ? 's' : ''})</span>
                      <span>₹{parking2wAmt.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span>₹{gstAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-base pt-2 border-t border-primary/10">
                    <span>Slot Total</span>
                    <span className="text-primary">₹{total.toFixed(2)}</span>
                  </div>
                  {applyOffer && (() => {
                    const preview = slotOfferPreview(priced, promo);
                    if (!preview) return null;
                    return (
                      <div className={`p-2 rounded text-xs ${preview.ok ? 'bg-amber-500/15 text-amber-900 dark:text-amber-200' : 'bg-muted text-muted-foreground'}`} data-testid={`walkin-offer-preview-${index}`}>
                        {preview.ok ? '🎁 ' : ''}{preview.text}
                      </div>
                    );
                  })()}
                </div>
              )}
              <div className="space-y-2">
                <Label>Parking Add-ons <span className="text-muted-foreground font-normal text-xs">(Optional — per-venue rates)</span></Label>
                {(() => {
                  const venue = venues.find(v => v.id === slot.venue_id);
                  if (!venue) {
                    return <p className="text-xs text-muted-foreground italic">Select a venue to add parking.</p>;
                  }
                  const cfg = venue.parking_config || {};
                  const isDaily = true; // parking is billed per day for CM guest bookings
                  const types = [
                    { c: cfg.four_wheeler || {}, label: '4-Wheeler', qtyKey: 'parking_qty', rateKey: 'parking_4w_rate', tid: 'parking-qty', max: 10 },
                    { c: cfg.two_wheeler || {}, label: '2-Wheeler', qtyKey: 'parking_2w_qty', rateKey: 'parking_2w_rate', tid: 'parking-2w-qty', max: 20 },
                  ];
                  return (
                    <div className="grid grid-cols-2 gap-3">
                      {types.map(({ c, label, qtyKey, rateKey, tid, max }) => (
                        <div key={qtyKey} className={`p-3 rounded-lg border space-y-2 ${slot[qtyKey] > 0 ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium">{label}</p>
                              <p className="text-xs text-muted-foreground">
                                {c.enabled
                                  ? `₹${isDaily ? (c.daily_rate || 0) + '/day' : (c.hourly_rate || 0) + '/hr'} × spots${c.quantity ? ` · ${c.quantity} avail` : ''}`
                                  : 'Not configured — enter rate'}
                              </p>
                            </div>
                            <Input type="number" min={0} max={c.enabled ? (c.quantity || 999) : max} value={slot[qtyKey] || 0}
                              onChange={(e) => updateSlot(index, { [qtyKey]: Math.max(0, Math.min(c.enabled ? (c.quantity || 999) : 999, parseInt(e.target.value) || 0)) })}
                              className={`${theme.inputBg} w-16 h-8 text-sm text-center`} data-testid={`walkin-${tid}-${index}`} />
                          </div>
                          {!c.enabled && slot[qtyKey] > 0 && (
                            <div className="flex items-center gap-2">
                              <Label className="text-xs whitespace-nowrap">₹ {isDaily ? '/ day' : '/ hr'}</Label>
                              <Input type="number" min={0} value={slot[rateKey] ?? ''} placeholder="Rate"
                                onChange={(e) => updateSlot(index, { [rateKey]: e.target.value })}
                                className={`${theme.inputBg} h-8 text-sm`} data-testid={`walkin-${tid.replace('qty', 'rate')}-${index}`} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}

        <Button type="button" variant="outline" onClick={addSlot} className={`w-full border-dashed ${theme.cardBorder}`} data-testid="walkin-add-slot-btn">
          <Plus className="w-4 h-4 mr-1.5" />Add New Slot
        </Button>

        {slots.length > 1 && (
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 flex justify-between font-semibold text-base" data-testid="walkin-grand-total">
            <span>Grand Total ({slots.length} slots)</span>
            <span className="text-primary">₹{grandTotal.toFixed(2)}</span>
          </div>
        )}

        <div className="space-y-2">
          <Label>Payment Method</Label>
          <Select value={form.payment_method || 'razorpay'} onValueChange={(v) => setForm({...form, payment_method: v})}>
            <SelectTrigger className={theme.inputBg}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="razorpay">Razorpay (Send Payment Link)</SelectItem>
              <SelectItem value="manual">Manual / Cash Payment</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="walkin-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {form.payment_method === 'manual' ? 'Create Booking' : 'Create & Send Payment Link'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

const EMPTY_CREDIT_SALE = { customer_name: '', customer_email: '', customer_phone: '', credits: '', payment_method: 'razorpay', payment_reference: '' };

// Sell a credit tier to a walk-in at the online price, with the sale bonus
// (e.g. 2X credits) while it runs — POST /payments/desk-credit-sale.
// `onLinkSent(result)` shows the payment link; cash sales just close.
export const SellCreditsModal = ({ open, onClose, onLinkSent, theme }) => {
  const { api } = useAuth();
  const promo = usePromotion();
  const [form, setForm] = useState(EMPTY_CREDIT_SALE);
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const lookup = useOfferCustomer(open ? form.customer_email : '');

  useEffect(() => {
    if (!open || tiers.length) return;
    api.get('/credits/tiers').then(r => setTiers(r.data.tiers || [])).catch(() => toast.error('Could not load credit tiers'));
  }, [open, api, tiers.length]);

  const tier = tiers.find(t => String(t.credits) === String(form.credits));
  const bonusLive = offerLive(CREDITS_2X_OFFER);
  const multiplier = bonusLive ? (promo.offers[CREDITS_2X_OFFER].multiplier || 1) : 1;
  const bonusFor = (credits) => Math.max(0, Math.round(credits * (multiplier - 1)));
  const close = () => { setForm(EMPTY_CREDIT_SALE); onClose(false); };

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    if (!lookup.looksValid) { toast.error("Customer email is required. The credits go to their account."); return; }
    if (lookup.data && !lookup.data.eligible) { toast.error(lookup.data.reason); return; }
    if (!tier) { toast.error('Choose a credit pack'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/payments/desk-credit-sale', {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim() || null,
        credits: tier.credits,
        payment_method: form.payment_method,
        payment_reference: form.payment_reference.trim() || null,
      });
      const accountNote = data.account_created ? ' New account created; set-password email sent.' : '';
      if (data.payment_link) {
        onLinkSent({
          message: data.message + accountNote,
          payment_link: data.payment_link,
          total_amount: data.total_amount,
          guest_name: data.customer_name || form.customer_name,
          guest_email: data.customer_email,
          guest_phone: form.customer_phone,
          sale_offer: data.bonus_credits ? { rewards: [{ summary: `${data.bonus_credits} bonus credits (${promo?.name || 'sale'} 2X)` }] } : null,
        });
      } else {
        toast.success(data.message + accountNote);
      }
      close();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sell credits');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-md max-h-[85vh] overflow-y-auto`}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Coins className="w-5 h-5 text-primary" />Sell Credits to a Walk-in</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          {bonusLive && (
            <div className="p-3 rounded-lg border border-amber-500/50 bg-amber-500/10 text-xs" data-testid="sell-credits-offer">
              🪔 <strong>{promo.name}: {multiplier}X credits</strong>, same price as online. Bonus credits never expire.
            </div>
          )}
          <div className="space-y-2">
            <Label>Customer Name *</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={theme.inputBg} placeholder="Enter customer name" data-testid="sell-credits-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className={theme.inputBg} placeholder="customer@email.com" data-testid="sell-credits-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className={theme.inputBg} placeholder="+91 98765 43210" />
            </div>
          </div>
          <OfferAccountStatus email={form.customer_email} lookup={lookup} what="credits" />
          <div className="space-y-2">
            <Label>Credit Pack *</Label>
            <Select value={String(form.credits)} onValueChange={(v) => setForm({ ...form, credits: v })}>
              <SelectTrigger className={theme.inputBg} data-testid="sell-credits-tier"><SelectValue placeholder="Choose a pack" /></SelectTrigger>
              <SelectContent>
                {tiers.map(t => (
                  <SelectItem key={t.credits} value={String(t.credits)}>
                    {t.credits} credits{bonusFor(t.credits) ? ` + ${bonusFor(t.credits)} FREE` : ''} · ₹{Math.round(t.total_amount).toLocaleString('en-IN')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {tier && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{tier.credits} credits ({tier.label}{tier.discount_percent ? `, ${tier.discount_percent}% off` : ''})</span><span>₹{tier.discounted_amount.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span>₹{tier.gst_amount.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold pt-1.5 border-t border-primary/10"><span>Total</span><span className="text-primary">₹{tier.total_amount.toFixed(2)}</span></div>
              {bonusFor(tier.credits) > 0 && (
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300 pt-1">🎁 Customer gets {tier.credits + bonusFor(tier.credits)} credits ({tier.credits} + {bonusFor(tier.credits)} free)</p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger className={theme.inputBg}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="razorpay">Razorpay (Send Payment Link)</SelectItem>
                <SelectItem value="cash">Cash / UPI at the desk</SelectItem>
              </SelectContent>
            </Select>
            {form.payment_method === 'cash' && (
              <Input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} className={theme.inputBg} placeholder="Receipt / UPI reference (optional)" data-testid="sell-credits-reference" />
            )}
            <p className="text-xs text-muted-foreground">
              {form.payment_method === 'cash' ? 'Credits are added as soon as you confirm. Only confirm once the money has been received.' : 'Credits are added once the customer pays the link.'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} className={theme.cardBorder}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="sell-credits-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Coins className="w-4 h-4 mr-2" />}
            {form.payment_method === 'cash' ? 'Confirm Cash Sale' : 'Send Payment Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LogVisitorModal = ({ open, onClose, form, setForm, onSubmit, saving, venues, theme }) => {
  const isVendor = form.visitor_type === 'vendor';
  const isGuest = form.visitor_type === 'guest';
  const clientFieldsComplete = !!(
    form.venue_id &&
    form.product_enquiry &&
    form.visitor_name?.trim() &&
    form.visitor_email?.trim() &&
    form.visitor_phone?.trim() &&
    form.visit_date &&
    form.seats_required
  );
  const isSubmitDisabled = saving || (!isVendor && !isGuest && !clientFieldsComplete);
  return (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-lg max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Log a Visitor</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2">
          <Label>Visitor Type *</Label>
          <Select value={form.visitor_type || 'client'} onValueChange={(v) => setForm({...form, visitor_type: v})}>
            <SelectTrigger className={theme.inputBg} data-testid="visitor-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="vendor">Vendor</SelectItem>
              <SelectItem value="guest">Guest</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Venue *</Label>
          <Select value={form.venue_id} onValueChange={(v) => setForm({...form, venue_id: v})}>
            <SelectTrigger className={theme.inputBg} data-testid="visitor-venue-select"><SelectValue placeholder="Select Venue" /></SelectTrigger>
            <SelectContent>
              {venues.filter(v => v.is_active).map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {(isVendor || isGuest) ? (
          <>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={form.visitor_name} onChange={(e) => setForm({...form, visitor_name: e.target.value})} className={theme.inputBg} placeholder="Enter name" data-testid="visitor-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input value={form.visitor_phone} onChange={(e) => setForm({...form, visitor_phone: e.target.value})} className={theme.inputBg} placeholder="+91 98765 43210" data-testid="visitor-phone-input" />
            </div>
            {isGuest && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Person You Are Meeting *</Label>
                  <Input value={form.host_name || ''} onChange={(e) => setForm({...form, host_name: e.target.value})} className={theme.inputBg} placeholder="Employee/host name" data-testid="visitor-host-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input value={form.host_company || ''} onChange={(e) => setForm({...form, host_company: e.target.value})} className={theme.inputBg} placeholder="Company of person being visited" data-testid="visitor-host-company-input" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Entry Time *</Label>
              <Select value={form.start_time} onValueChange={(v) => setForm({...form, start_time: v})}>
                <SelectTrigger className={theme.inputBg} data-testid="visitor-start-time"><SelectValue placeholder="Entry" /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.notes || ''} onChange={(e) => setForm({...form, notes: e.target.value})} className={theme.inputBg} placeholder="Reason for visit..." data-testid="visitor-notes-input" />
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <Label>Product Enquiry *</Label>
              <Select value={form.product_enquiry || ''} onValueChange={(v) => setForm({...form, product_enquiry: v})}>
                <SelectTrigger className={theme.inputBg} data-testid="visitor-product-enquiry-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="meeting_room">Meeting Room</SelectItem>
                  <SelectItem value="conference_room">Conference Room</SelectItem>
                  <SelectItem value="day_pass">Day Pass</SelectItem>
                  <SelectItem value="dedicated_desk">Dedicated Desk</SelectItem>
                  <SelectItem value="dedicated_cabin">Dedicated Cabin</SelectItem>
                  <SelectItem value="event_space">Event Space</SelectItem>
                  <SelectItem value="virtual_office">Virtual Office</SelectItem>
                  <SelectItem value="podcast_spaces">Podcast Spaces</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Visitor Name *</Label>
              <Input value={form.visitor_name} onChange={(e) => setForm({...form, visitor_name: e.target.value})} className={theme.inputBg} placeholder="Enter visitor name" data-testid="visitor-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={form.visitor_email} onChange={(e) => setForm({...form, visitor_email: e.target.value})} className={theme.inputBg} placeholder="visitor@email.com" data-testid="visitor-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input value={form.visitor_phone} onChange={(e) => setForm({...form, visitor_phone: e.target.value})} className={theme.inputBg} placeholder="+91 98765 43210" data-testid="visitor-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visit Date *</Label>
                <Input type="date" value={form.visit_date || ''} onChange={(e) => { if (!validateNotSunday(e.target.value)) { toast.error('Closed on Sundays'); return; } setForm({...form, visit_date: e.target.value}); }} min={new Date().toISOString().split('T')[0]} className={theme.inputBg} data-testid="visitor-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Number of Seats Required *</Label>
                <Input type="number" min={1} value={form.seats_required || 1} onChange={(e) => setForm({...form, seats_required: Math.max(1, parseInt(e.target.value) || 1)})} className={theme.inputBg} data-testid="visitor-seats-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Purpose / Notes</Label>
              <Textarea value={form.notes || ''} onChange={(e) => setForm({...form, notes: e.target.value})} className={theme.inputBg} placeholder="Purpose of visit..." data-testid="visitor-notes-input" />
            </div>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={isSubmitDisabled} data-testid="visitor-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ClipboardList className="w-4 h-4 mr-2" />} Log Visitor
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export const BlockSlotModal = ({ open, onClose, form, setForm, onSubmit, saving, venues, bookables, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-md max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Ban className="w-5 h-5 text-amber-500" /> Block Time Slot</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2">
          <Label>Venue *</Label>
          <Select value={form.venue_id} onValueChange={(v) => setForm({...form, venue_id: v, bookable_id: ''})}>
            <SelectTrigger className={theme.inputBg}><SelectValue placeholder="Select Venue" /></SelectTrigger>
            <SelectContent>{venues.filter(v => v.is_active).map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Space *</Label>
          <Select value={form.bookable_id} onValueChange={(v) => setForm({...form, bookable_id: v})}>
            <SelectTrigger className={theme.inputBg}><SelectValue placeholder="Select Space" /></SelectTrigger>
            <SelectContent>{bookables.filter(b => b.venue_id === form.venue_id && (b.is_active || b.is_active_internal !== false)).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className={theme.inputBg} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} className={theme.inputBg} /></div>
          <div className="space-y-2"><Label>End Time</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className={theme.inputBg} /></div>
        </div>
        <div className="space-y-2"><Label>Reason *</Label><Textarea value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} className={theme.inputBg} placeholder="e.g., Maintenance, Cleaning, Event setup..." /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />} Block Slot
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AddUserModal = ({ open, onClose, form, setForm, onSubmit, saving, organizations, theme }) => {
  const [showPassword, setShowPassword] = useState(false);
  return (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-md max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Add New User</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={theme.inputBg} /></div>
        <div className="space-y-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={theme.inputBg} /></div>
        <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={theme.inputBg} /></div>
        <div className="space-y-2">
          <Label>Password *</Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
              className={`${theme.inputBg} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Organization (Optional)</Label>
          <Select value={form.organization_id || "none"} onValueChange={(v) => setForm({...form, organization_id: v === "none" ? "" : v})}>
            <SelectTrigger className={theme.inputBg}><SelectValue placeholder="Select Organization" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None (B2C User)</SelectItem>
              {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} Add User
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export const AddOrgModal = ({ open, onClose, form, setForm, onSubmit, saving, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-md max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Add New Organization</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2"><Label>Organization Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={theme.inputBg} /></div>
        <div className="space-y-2"><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({...form, gstin: e.target.value})} className={theme.inputBg} placeholder="22AAAAA0000A1Z5" /></div>
        <div className="space-y-2"><Label>Billing Email *</Label><Input type="email" value={form.billing_email} onChange={(e) => setForm({...form, billing_email: e.target.value})} className={theme.inputBg} /></div>
        <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={theme.inputBg} /></div>
        <div className="space-y-2"><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} className={theme.inputBg} /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Add Organization
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AllocateCreditsModal = ({ open, onClose, form, setForm, onSubmit, saving, organizations, users, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-lg max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Allocate Credits</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2">
          <Label>Allocate To</Label>
          <Select value={form.target_type} onValueChange={(v) => setForm({...form, target_type: v, target_id: ''})}>
            <SelectTrigger className={theme.inputBg}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="organization">Organization</SelectItem>
              <SelectItem value="user">Individual User</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{form.target_type === 'organization' ? 'Organization' : 'User'} *</Label>
          <Select value={form.target_id || "none"} onValueChange={(v) => setForm({...form, target_id: v === "none" ? "" : v})}>
            <SelectTrigger className={theme.inputBg}><SelectValue placeholder={`Select ${form.target_type}`} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>Select {form.target_type}</SelectItem>
              {form.target_type === 'organization'
                ? organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)
                : users.filter(u => u.role === 'client').map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)
              }
            </SelectContent>
          </Select>
        </div>

        {/* Section 1: Monthly Credits */}
        <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-primary" />
            <Label className="text-sm font-semibold">Monthly Credits (Auto-Renewal)</Label>
          </div>
          <p className="text-xs text-muted-foreground">Credits auto-added based on the renewal cycle</p>
          <div className="space-y-2">
            <Label className="text-xs">Credits per Cycle</Label>
            <Input type="number" min={0} placeholder="e.g., 50" value={form.monthly_credits || ''} onChange={(e) => setForm({...form, monthly_credits: parseInt(e.target.value) || 0})} className={theme.inputBg} data-testid="monthly-credits-input" />
          </div>
          {form.monthly_credits > 0 && (
            <>
              <div className="space-y-2">
                <Label className="text-xs">Carry Forward Credits (%)</Label>
                <Select value={String(form.carry_forward_percentage || 0)} onValueChange={(v) => setForm({...form, carry_forward_percentage: parseInt(v)})}>
                  <SelectTrigger className={theme.inputBg} data-testid="carry-forward-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% — No carry forward</SelectItem>
                    <SelectItem value="25">25%</SelectItem>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="75">75%</SelectItem>
                    <SelectItem value="100">100% — Full carry forward</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">How much of unused credits carries over to the next cycle</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Renewal Cycle</Label>
                <Select value={String(form.renewal_cycle_months || 1)} onValueChange={(v) => setForm({...form, renewal_cycle_months: parseInt(v)})}>
                  <SelectTrigger className={theme.inputBg} data-testid="renewal-cycle-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every 1 month</SelectItem>
                    <SelectItem value="2">Every 2 months</SelectItem>
                    <SelectItem value="3">Every 3 months</SelectItem>
                    <SelectItem value="6">Every 6 months</SelectItem>
                    <SelectItem value="12">Every 12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Expiry Date (Auto-Renewal Stops)</Label>
                <Input type="month" value={form.monthly_valid_till || ''} onChange={(e) => setForm({...form, monthly_valid_till: e.target.value})} className={theme.inputBg} data-testid="monthly-valid-till" />
                <p className="text-xs text-muted-foreground">Auto-renewal stops after this month. Leave blank for no expiry.</p>
              </div>
            </>
          )}
        </div>

        {/* Section 2: Additional Credit Package */}
        <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 space-y-3">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-amber-600" />
            <Label className="text-sm font-semibold">Additional Credit Package</Label>
          </div>
          <p className="text-xs text-muted-foreground">One-time credits — no monthly renewal. Expires on the validity date.</p>
          <div className="space-y-2">
            <Label className="text-xs">Credits (One-Time)</Label>
            <Input type="number" min={0} placeholder="e.g., 200" value={form.additional_credits || ''} onChange={(e) => setForm({...form, additional_credits: parseInt(e.target.value) || 0})} className={theme.inputBg} data-testid="additional-credits-input" />
          </div>
          {form.additional_credits > 0 && (
            <div className="space-y-2">
              <Label className="text-xs">Valid Till (Month & Year) *</Label>
              <Input type="month" value={form.additional_valid_till || ''} onChange={(e) => setForm({...form, additional_valid_till: e.target.value})} className={theme.inputBg} data-testid="additional-valid-till" />
              <p className="text-xs text-muted-foreground">Credits expire at the end of this month</p>
            </div>
          )}
        </div>

        {(form.monthly_credits > 0 || form.additional_credits > 0) && (
          <div className="p-3 rounded-lg bg-secondary/50 border border-border text-xs space-y-1">
            {form.monthly_credits > 0 && (
              <>
                <p><span className="font-medium">Monthly:</span> {form.monthly_credits} credits every {form.renewal_cycle_months || 1} month(s){form.monthly_valid_till ? `, expires ${form.monthly_valid_till}` : ', no expiry'}</p>
                <p><span className="font-medium">Carry Forward:</span> {form.carry_forward_percentage || 0}% of unused credits</p>
              </>
            )}
            {form.additional_credits > 0 && (
              <p><span className="font-medium">Additional:</span> {form.additional_credits} one-time credits{form.additional_valid_till ? `, valid till ${form.additional_valid_till}` : ', no expiry'}</p>
            )}
            <p className="font-medium mt-1">Total credits to add now: {(form.monthly_credits || 0) + (form.additional_credits || 0)}</p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="allocate-credits-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Coins className="w-4 h-4 mr-2" />} Allocate Credits
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const InternalBookingModal = ({ open, onClose, form, setForm, onSubmit, saving, venues, bookables, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-md max-h-[85vh] overflow-y-auto`} data-testid="internal-booking-modal">
      <DialogHeader>
        <DialogTitle className="font-heading">Book Internally</DialogTitle>
        <p className="text-sm text-muted-foreground">For Clayworks internal team — no payment required</p>
      </DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2"><Label>Booked By (Name) *</Label><Input placeholder="Full name" value={form.booked_by_name} onChange={(e) => setForm({...form, booked_by_name: e.target.value})} className={theme.inputBg} data-testid="internal-name" /></div>
        <div className="space-y-2"><Label>Email</Label><Input type="email" placeholder="Email address" value={form.booked_by_email} onChange={(e) => setForm({...form, booked_by_email: e.target.value})} className={theme.inputBg} data-testid="internal-email" /></div>
        <div className="space-y-2">
          <Label>Venue *</Label>
          <Select value={form.venue_id || "none"} onValueChange={(v) => setForm({...form, venue_id: v === "none" ? "" : v, bookable_id: ''})}>
            <SelectTrigger className={theme.inputBg} data-testid="internal-venue"><SelectValue placeholder="Select venue" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>Select venue</SelectItem>
              {venues.filter(v => v.is_active).map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Space *</Label>
          <Select value={form.bookable_id || "none"} onValueChange={(v) => setForm({...form, bookable_id: v === "none" ? "" : v})}>
            <SelectTrigger className={theme.inputBg} data-testid="internal-bookable"><SelectValue placeholder="Select space" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>Select space</SelectItem>
              {bookables.filter(b => b.venue_id === form.venue_id && (b.is_active || b.is_active_internal !== false)).map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.capacity} seats)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Date *</Label><Input type="date" value={form.booking_date} onChange={(e) => setForm({...form, booking_date: e.target.value})} className={theme.inputBg} data-testid="internal-date" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Start Time *</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} className={theme.inputBg} data-testid="internal-start-time" /></div>
          <div className="space-y-2"><Label>End Time *</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className={theme.inputBg} data-testid="internal-end-time" /></div>
        </div>
        <div className="space-y-2"><Label>Notes</Label><Input placeholder="Purpose of booking..." value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className={theme.inputBg} data-testid="internal-notes" /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="internal-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-4 h-4 mr-2" />} Book Internally
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


export const WalletViewerModal = ({ open, onClose, walletData, targetName, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden`}>
      <DialogHeader><DialogTitle className="font-heading">Credit Wallet — {targetName}</DialogTitle></DialogHeader>
      {walletData ? (
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className={`p-3 min-w-0 rounded-lg ${theme.inputBg} text-center`}>
              <p className="text-xs text-muted-foreground">Monthly Balance</p>
              <p className="text-2xl font-bold text-primary">{(walletData.balance || 0).toFixed(1)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">expires monthly</p>
            </div>
            <div className={`p-3 min-w-0 rounded-lg ${theme.inputBg} text-center`}>
              <p className="text-xs text-muted-foreground">Top-up Balance</p>
              <p className="text-2xl font-bold text-blue-600">{(walletData.topup_balance || 0).toFixed(1)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">never expires</p>
            </div>
            <div className={`p-3 min-w-0 rounded-lg ${theme.inputBg} text-center border-2 border-primary/30`}>
              <p className="text-xs text-muted-foreground">Total Available</p>
              <p className="text-2xl font-bold text-green-600">{(walletData.total_balance ?? ((walletData.balance || 0) + (walletData.topup_balance || 0))).toFixed(1)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">usable now</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className={`p-3 min-w-0 rounded-lg ${theme.inputBg} text-center`}>
              <p className="text-xs text-muted-foreground">Monthly Allocation</p>
              <p className="text-xl font-bold">{walletData.monthly_allocation || 0}</p>
            </div>
            <div className={`p-3 min-w-0 rounded-lg ${theme.inputBg} text-center`}>
              <p className="text-xs text-muted-foreground">Used This Month</p>
              <p className="text-xl font-bold text-red-500">{walletData.monthly_used?.toFixed(1) || 0}</p>
            </div>
            <div className={`p-3 min-w-0 rounded-lg ${theme.inputBg} text-center`}>
              <p className="text-xs text-muted-foreground">Added This Month</p>
              <p className="text-xl font-bold text-green-500">{walletData.monthly_added?.toFixed(1) || 0}</p>
            </div>
          </div>
          {walletData.valid_until && (
            <p className="text-xs text-muted-foreground">Valid until: <span className="font-medium">{new Date(walletData.valid_until).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</span></p>
          )}
          {walletData.auto_renew && (
            <div className="p-2.5 rounded-lg bg-green-500/10 border border-green-500/20 space-y-1">
              <p className="text-xs text-green-600 font-medium">Auto-renewal active</p>
              {walletData.renewal_cycle_months > 1 && <p className="text-xs text-muted-foreground">Cycle: Every {walletData.renewal_cycle_months} months</p>}
              <p className="text-xs text-muted-foreground">Unused monthly credits reset on the 1st of each month (no carry forward)</p>
              {walletData.renewal_expiry_date && <p className="text-xs text-muted-foreground">Expires: {walletData.renewal_expiry_date}</p>}
            </div>
          )}

          <div>
            <p className="text-sm font-medium mb-2">Transaction History</p>
            <div className="space-y-2 max-h-[250px] overflow-y-auto overflow-x-hidden">
              {walletData.transactions?.length > 0 ? walletData.transactions.map((tx, i) => (
                <div key={i} className={`p-2.5 rounded-lg ${theme.inputBg} flex items-start justify-between gap-2 text-sm`}>
                  <div className="flex-1 min-w-0">
                    <p className="break-words">{tx.description}</p>
                    <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <span className={`ml-2 font-semibold whitespace-nowrap ${tx.amount > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(1)}
                  </span>
                </div>
              )) : <p className="text-sm text-muted-foreground text-center py-4">No transactions yet</p>}
            </div>
          </div>
        </div>
      ) : (
        <div className="py-8 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <p className="text-sm">Loading wallet...</p>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Close</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AdjustCreditsModal = ({ open, onClose, form, setForm, onSubmit, saving, targetName, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-sm max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Adjust Credits — {targetName}</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2">
          <Label>Current Balance</Label>
          <p className="text-lg font-bold">{form.current_balance?.toFixed(1)} credits</p>
        </div>
        <div className="space-y-2">
          <Label>New Balance *</Label>
          <Input type="number" min={0} value={form.new_balance ?? ''} onChange={(e) => setForm({...form, new_balance: parseFloat(e.target.value) || 0})} className={theme.inputBg} data-testid="adjust-balance-input" />
        </div>
        <div className="space-y-2">
          <Label>Reason</Label>
          <Input placeholder="e.g., Correction for duplicate allocation" value={form.reason || ''} onChange={(e) => setForm({...form, reason: e.target.value})} className={theme.inputBg} data-testid="adjust-reason-input" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="adjust-credits-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />} Adjust
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DeleteConfirmModal = ({ open, onClose, onConfirm, saving, itemName, itemType, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-sm`}>
      <DialogHeader><DialogTitle className="font-heading text-red-500">Delete {itemType}?</DialogTitle></DialogHeader>
      <p className="text-sm text-muted-foreground py-2">
        Are you sure you want to delete <span className="font-semibold text-foreground">{itemName}</span>? This action cannot be undone.
      </p>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button variant="destructive" onClick={onConfirm} disabled={saving} data-testid="confirm-delete-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />} Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


export const EditOrgModal = ({ open, onClose, form, setForm, onSubmit, saving, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-md max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Edit Organization</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2">
          <Label>Organization Name *</Label>
          <Input value={form.name || ''} onChange={(e) => setForm({...form, name: e.target.value})} className={theme.inputBg} data-testid="edit-org-name" />
        </div>
        <div className="space-y-2">
          <Label>POC Name</Label>
          <Input value={form.poc_name || ''} onChange={(e) => setForm({...form, poc_name: e.target.value})} className={theme.inputBg} data-testid="edit-org-poc-name" />
        </div>
        <div className="space-y-2">
          <Label>POC Email</Label>
          <Input type="email" value={form.poc_email || ''} onChange={(e) => setForm({...form, poc_email: e.target.value})} className={theme.inputBg} data-testid="edit-org-poc-email" />
        </div>
        <div className="space-y-2">
          <Label>POC Phone</Label>
          <Input value={form.poc_phone || ''} onChange={(e) => setForm({...form, poc_phone: e.target.value})} className={theme.inputBg} data-testid="edit-org-poc-phone" />
        </div>
        <div className="space-y-2">
          <Label>GST Number</Label>
          <Input value={form.gst_number || ''} onChange={(e) => setForm({...form, gst_number: e.target.value})} className={theme.inputBg} />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="edit-org-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />} Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const EditUserModal = ({ open, onClose, form, setForm, onSubmit, saving, organizations, theme }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-md max-h-[85vh] overflow-y-auto`}>
      <DialogHeader><DialogTitle className="font-heading">Edit User</DialogTitle></DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input value={form.name || ''} onChange={(e) => setForm({...form, name: e.target.value})} className={theme.inputBg} data-testid="edit-user-name" />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input type="email" value={form.email || ''} onChange={(e) => setForm({...form, email: e.target.value})} className={theme.inputBg} data-testid="edit-user-email" />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input value={form.phone || ''} onChange={(e) => setForm({...form, phone: e.target.value})} className={theme.inputBg} />
        </div>
        <div className="space-y-2">
          <Label>Organization</Label>
          <Select value={form.organization_id || "none"} onValueChange={(v) => setForm({...form, organization_id: v === "none" ? "" : v})}>
            <SelectTrigger className={theme.inputBg}><SelectValue placeholder="No Organization" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Organization</SelectItem>
              {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="edit-user-active" checked={form.is_active !== false} onCheckedChange={(v) => setForm({...form, is_active: !!v})} />
          <Label htmlFor="edit-user-active" className="text-sm cursor-pointer">Active</Label>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="edit-user-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />} Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


export const InternalClientModal = ({ open, onClose, form, setForm, onSubmit, saving, venues, bookables, organizations, users, theme, orgCreditInfo, creditRates, holidayDates = {}, user }) => {
  usePromotion(); // re-price once sale promotion state loads
  const orgUsers = form.organization_id
    ? users.filter(u => u.organization_id === form.organization_id)
    : [];
  // Internal-client bookings may be made on Sundays, but only by Admin/Centre
  // Manager/Cluster Manager — the backend re-validates this on submit.
  const canBookSunday = ['admin', 'center_manager', 'cluster_manager'].includes(user?.role);
  const selectedBookable = bookables.find(b => b.id === form.bookable_id);
  const selectedVenue = venues.find(v => v.id === form.venue_id);
  const hours = calcDurationHours(form.start_time, form.end_time);
  const mode = selectedBookable?.booking_mode || 'hourly';
  const rate = selectedBookable?.hourly_rate || 0;
  const passQty = 1;
  const passDays = 1;
  const baseAmount = mode === 'hourly' ? rate * hours : rate;
  const autoDisc = mode === 'hourly' ? getAutoDiscount(hours) : { percent: 0, label: '' };
  const autoDiscountAmt = baseAmount * autoDisc.percent / 100;
  const afterAutoDiscount = baseAmount - autoDiscountAmt;
  const discountPct = Math.max(0, Math.min(100, parseFloat(form.discount_percent) || 0));
  const manualDiscountAmt = afterAutoDiscount * discountPct / 100;
  const discountedBase = afterAutoDiscount - manualDiscountAmt;
  const gstAmount = discountedBase * 0.18;
  const calculatedTotal = discountedBase + gstAmount;

  // Credit calculation
  const creditBalance = (orgCreditInfo?.balance || 0) + (orgCreditInfo?.topup_balance || 0);
  const hasCredits = creditBalance > 0;

  const getVenueCreditRate = (venueName) => {
    if (!venueName || !creditRates) return creditRates?.default_rate || 1.5;
    const rates = creditRates.rates || {};
    const name = venueName.toLowerCase();
    for (const [keyword, r] of Object.entries(rates)) {
      if (name.includes(keyword.toLowerCase())) return r;
    }
    return creditRates.default_rate || 1.5;
  };

  const venueRate = selectedVenue ? getVenueCreditRate(selectedVenue.name) : 0;
  const roomCapacity = selectedBookable?.capacity || 1;
  // Mode-aware credit calculation (matches backend models.calculate_booking_credits):
  // - daily / monthly: credits = price / 100 (flat)
  // - hourly: credits = venueRate × capacity × hours
  const creditsRequired = (mode === 'daily' || mode === 'monthly')
    ? Math.round((rate / 100) * 100) / 100
    : venueRate * roomCapacity * (hours || 0);
  const canPayWithCredits = hasCredits && creditsRequired > 0 && creditBalance >= creditsRequired;

  return (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className={`${theme.cardBg} ${theme.cardBorder} max-w-lg max-h-[85vh] overflow-y-auto`}>
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-primary" /> Book for Internal Client
        </DialogTitle>
        <p className="text-sm text-muted-foreground">Book on behalf of an existing organization member. Credits will be auto-deducted if available.</p>
      </DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Organization *</Label>
            <Select value={form.organization_id || "none"} onValueChange={(v) => setForm({...form, organization_id: v === "none" ? "" : v, user_id: ''})}>
              <SelectTrigger className={theme.inputBg} data-testid="ic-org-select"><SelectValue placeholder="Select Organization" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" disabled>Select Organization</SelectItem>
                {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>User</Label>
            <Select value={form.user_id || "none"} onValueChange={(v) => setForm({...form, user_id: v === "none" ? "" : v})}>
              <SelectTrigger className={theme.inputBg} data-testid="ic-user-select"><SelectValue placeholder="Select User" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any / Organization</SelectItem>
                {orgUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Venue *</Label>
            <Select value={form.venue_id} onValueChange={(v) => setForm({...form, venue_id: v, bookable_id: ''})}>
              <SelectTrigger className={theme.inputBg} data-testid="ic-venue-select"><SelectValue placeholder="Select Venue" /></SelectTrigger>
              <SelectContent>
                {venues.filter(v => v.is_active).map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Space *</Label>
            <Select value={form.bookable_id} onValueChange={(v) => {
              const sel = bookables.find(b => b.id === v);
              const updates = { bookable_id: v };
              if (sel?.booking_mode === 'daily') { updates.start_time = '09:00'; updates.end_time = '20:00'; }
              else if (sel?.booking_mode === 'monthly') { updates.start_time = '09:00'; updates.end_time = '18:00'; }
              else { updates.start_time = ''; updates.end_time = ''; }
              setForm({...form, ...updates});
            }}>
              <SelectTrigger className={theme.inputBg} data-testid="ic-bookable-select"><SelectValue placeholder="Select Space" /></SelectTrigger>
              <SelectContent>
                {bookables.filter(b => b.venue_id === form.venue_id && (b.is_active || b.is_active_internal !== false)).map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} — ₹{b.hourly_rate}{b.booking_mode === 'daily' ? '/day' : b.booking_mode === 'monthly' ? '/month' : '/hr'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={form.booking_date || ''} onChange={(e) => { if (!canBookSunday && !validateNotSunday(e.target.value)) { toast.error('Closed on Sundays'); return; } if (holidayDates[e.target.value]) { toast.error(`Closed on ${e.target.value} (${holidayDates[e.target.value]})`); return; } setForm({...form, booking_date: e.target.value}); }} min={new Date().toISOString().split('T')[0]} className={theme.inputBg} data-testid="ic-date-input" />
        </div>
        {mode === 'daily' ? (
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20" data-testid="ic-daypass-info">
            <p className="text-sm font-medium">Day Pass: 9:00 AM - 8:00 PM</p>
            <p className="text-xs text-muted-foreground">Fixed timing for all day passes</p>
          </div>
        ) : mode === 'monthly' ? (
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20" data-testid="ic-monthly-info">
            <p className="text-sm font-medium">Dedicated Desk — Monthly</p>
            <p className="text-xs text-muted-foreground">Full month access, no time selection needed</p>
            {form.booking_date && (
              <p className="text-xs font-medium text-primary mt-1.5">Access: {monthlyPeriodLabel(form.booking_date)} (1 month)</p>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Time *</Label>
            <Select value={form.start_time} onValueChange={(v) => setForm({...form, start_time: v, end_time: ''})}>
              <SelectTrigger className={theme.inputBg} data-testid="ic-start-time"><SelectValue placeholder="Start" /></SelectTrigger>
              <SelectContent>
                {TIME_SLOTS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>End Time *</Label>
            <Select value={form.end_time} onValueChange={(v) => setForm({...form, end_time: v})}>
              <SelectTrigger className={theme.inputBg} data-testid="ic-end-time"><SelectValue placeholder="End" /></SelectTrigger>
              <SelectContent>
                {getEndSlots(form.start_time).map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        )}

        {selectedBookable && form.start_time && form.end_time && (mode !== 'hourly' || hours >= 1) && (
          <div className="space-y-3">
            {/* Credit Info Banner */}
            {form.organization_id && (
              <div className={`p-3 rounded-lg border ${canPayWithCredits ? 'bg-green-50 border-green-200' : hasCredits ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="ic-credit-info">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className={`w-4 h-4 ${canPayWithCredits ? 'text-green-600' : hasCredits ? 'text-amber-600' : 'text-red-500'}`} />
                    <span className="text-sm font-medium">
                      {canPayWithCredits
                        ? `${creditsRequired.toFixed(2)} credits will be deducted`
                        : hasCredits
                        ? `Insufficient credits (need ${creditsRequired.toFixed(2)}, have ${creditBalance.toFixed(2)})`
                        : 'No credits available — payment link will be generated'
                      }
                    </span>
                  </div>
                  {hasCredits && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white/70">
                      Balance: {creditBalance.toFixed(1)}
                    </span>
                  )}
                </div>
                {canPayWithCredits && (
                  <p className="text-xs text-green-700 mt-1">
                    {mode === 'daily'
                      ? `Day Pass: ₹${rate} ÷ 100 = ${creditsRequired.toFixed(2)} credits | Remaining: ${(creditBalance - creditsRequired).toFixed(2)}`
                      : mode === 'monthly'
                      ? `Monthly Desk: ₹${rate} ÷ 100 = ${creditsRequired.toFixed(2)} credits | Remaining: ${(creditBalance - creditsRequired).toFixed(2)}`
                      : `${venueRate} credit/seat/hr × ${roomCapacity} seats × ${hours.toFixed(0)} hrs = ${creditsRequired.toFixed(1)} credits | Remaining: ${(creditBalance - creditsRequired).toFixed(1)}`
                    }
                  </p>
                )}
              </div>
            )}

            {/* Amount breakdown (always shown for reference) */}
            {!canPayWithCredits && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Base ({mode === 'hourly' ? `${hours.toFixed(2)}hrs × ₹${rate}` : mode === 'daily' ? `${passQty} pass × ₹${rate}` : 'Monthly Rate'})</span>
                  <span>₹{baseAmount.toFixed(2)}</span>
                </div>
                {autoDisc.percent > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>{autoDisc.label}</span>
                    <span>-₹{autoDiscountAmt.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-primary/10">
                  <Label className="text-sm">Discount (%)</Label>
                  <Input type="number" min={0} max={100} step="0.1" value={form.discount_percent ?? ''} onChange={(e) => { const v = parseFloat(e.target.value) || 0; setForm({...form, discount_percent: Math.max(0, Math.min(100, v))}); }} className={`${theme.inputBg} w-24 h-8 text-sm`} placeholder="0" data-testid="ic-discount-input" />
                </div>
                {discountPct > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Manual Discount ({discountPct}%)</span>
                    <span>-₹{manualDiscountAmt.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Discounted Base</span>
                  <span>₹{discountedBase.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">GST (18%)</span>
                  <span>₹{gstAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-base pt-2 border-t border-primary/10">
                  <span>Total</span>
                  <span className="text-primary">₹{calculatedTotal.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label>Notes</Label>
          <Input value={form.notes || ''} onChange={(e) => setForm({...form, notes: e.target.value})} className={theme.inputBg} placeholder="Purpose of booking..." data-testid="ic-notes" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} className={theme.cardBorder}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} data-testid="ic-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Briefcase className="w-4 h-4 mr-2" />}
          Book for Client
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};
